import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Mail, Phone, MessageSquare, Send, CheckCircle, Clock, Inbox } from 'lucide-react';
import { enquiriesAPI } from '../services/api';
import { toast } from 'sonner';

const EnquiryForm = ({ professionalId, professionalName }) => {
  const [formData, setFormData] = useState({
    subject: '',
    message: '',
    email: '',
    phone: '',
    preferredContact: 'email'
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [sentEnquiries, setSentEnquiries] = useState([]);
  const [activeTab, setActiveTab] = useState('send');

  useEffect(() => {
    loadSentEnquiries();
  }, []);

  const loadSentEnquiries = async () => {
    try {
      const res = await enquiriesAPI.getSentEnquiries();
      const filtered = (res.data?.enquiries || []).filter(e => e.professionalId === professionalId);
      setSentEnquiries(filtered);
    } catch (e) {
      console.error('Error loading sent enquiries:', e);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.subject || !formData.message) {
      toast.error('Please fill in subject and message');
      return;
    }

    try {
      setSending(true);
      await enquiriesAPI.createEnquiry({ professionalId, ...formData });
      toast.success(`Enquiry sent to ${professionalName}!`);
      setSent(true);
      loadSentEnquiries();
      setTimeout(() => {
        setFormData({ subject: '', message: '', email: '', phone: '', preferredContact: 'email' });
        setSent(false);
      }, 3000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send enquiry');
    } finally {
      setSending(false);
    }
  };

  const formatDate = (d) => {
    if (!d) return '';
    return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center">
          <MessageSquare className="w-5 h-5 mr-2 text-primary" />
          Enquiry — {professionalName}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-2 mb-4">
            <TabsTrigger value="send"><Send className="w-4 h-4 mr-1" /> New Enquiry</TabsTrigger>
            <TabsTrigger value="sent" className="relative">
              <Inbox className="w-4 h-4 mr-1" /> Sent ({sentEnquiries.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="send">
            {sent ? (
              <div className="text-center py-8">
                <CheckCircle className="w-14 h-14 mx-auto mb-3 text-primary" />
                <h3 className="text-lg font-bold mb-1">Enquiry Sent!</h3>
                <p className="text-sm text-muted-foreground">{professionalName} will get back to you soon.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="subject">Subject *</Label>
                  <Input id="subject" value={formData.subject} onChange={(e) => setFormData({ ...formData, subject: e.target.value })} placeholder="What is your enquiry about?" required maxLength={100} data-testid="enquiry-subject" />
                </div>
                <div>
                  <Label htmlFor="message">Message *</Label>
                  <Textarea id="message" value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })} placeholder="Provide details about your enquiry..." required rows={5} maxLength={1000} data-testid="enquiry-message" />
                  <p className="text-xs text-muted-foreground mt-1">{formData.message.length}/1000</p>
                </div>
                <div>
                  <Label htmlFor="email">Email (Optional)</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 w-4 h-4 text-muted-foreground" />
                    <Input id="email" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="your.email@example.com" className="pl-10" data-testid="enquiry-email" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="phone">Phone (Optional)</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-3 w-4 h-4 text-muted-foreground" />
                    <Input id="phone" type="tel" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="+44 1234 567890" className="pl-10" data-testid="enquiry-phone" />
                  </div>
                </div>
                <div>
                  <Label>Preferred Contact</Label>
                  <Select value={formData.preferredContact} onValueChange={(v) => setFormData({ ...formData, preferredContact: v })}>
                    <SelectTrigger data-testid="enquiry-contact-method"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="email">Email</SelectItem>
                      <SelectItem value="phone">Phone</SelectItem>
                      <SelectItem value="either">Either</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button type="submit" disabled={sending} className="w-full btn-primary" data-testid="enquiry-submit">
                  {sending ? <><Send className="w-4 h-4 mr-2 animate-pulse" /> Sending...</> : <><Send className="w-4 h-4 mr-2" /> Send Enquiry</>}
                </Button>
              </form>
            )}
          </TabsContent>

          <TabsContent value="sent">
            {sentEnquiries.length === 0 ? (
              <div className="text-center py-8">
                <Inbox className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">No enquiries sent to {professionalName} yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {sentEnquiries.map((eq) => (
                  <div key={eq._id} className="p-3 rounded-lg border border-border bg-muted/30">
                    <div className="flex items-center justify-between mb-1">
                      <p className="font-medium text-sm text-foreground">{eq.subject}</p>
                      <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${eq.status === 'pending' ? 'bg-purple-400/20 text-purple-400' : eq.status === 'responded' ? 'bg-primary/20 text-primary' : 'bg-muted text-muted-foreground'}`}>
                        {eq.status}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground line-clamp-2">{eq.message}</p>
                    <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                      <Clock className="w-3 h-3" />
                      {formatDate(eq.createdAt)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default EnquiryForm;
